
/**
 * Module dependencies.
 */

import { color, media, units } from 'src/styles/utils';
import { ifNotProp, ifProp, theme } from 'styled-tools';
import { useFiltersList } from 'src/context/filters-list/context';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import Fill from 'src/components/core/layout/fill';
import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState
} from 'react';

import Svg from 'src/components/core/svg';
import Type from 'src/components/core/typography/index';
import closeIcon from 'src/assets/svg/close.svg';
import isEmpty from 'lodash/isEmpty';
import qs from 'qs';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode,
  className?: string,
  isOpen: boolean,
  isUpdating: boolean,
  onClose: () => void,
  onUpdate: () => void
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled(Fill)<{ isOpen: boolean }>`
  overflow: hidden;
  position: fixed;
  transition: visibility ${theme('animations.easeOutQuadTransition')};
  visibility: visible;
  z-index: ${theme('zIndex.filters')};

  ${ifNotProp('isOpen', css`
    pointer-events: none;
    visibility: hidden;
  `)}
`;

/**
 * `Overlay` styled component.
 */

const Overlay = styled(Fill)<{ isOpen: boolean }>`
  background-color: ${color('black')};
  opacity: ${ifProp('isOpen', 0.3, 0)};
  transition: opacity ${theme('animations.easeOutQuadTransition')};

  ${media.max('ms')`
    display: none;
  `}
`;

/**
 * `Sidebar` styled component.
 */

const Sidebar = styled.div<{ isOpen: boolean }>`
  background-color: ${color('white')};
  bottom: 0;
  display: flex;
  flex-direction: column;
  position: fixed;
  right: 0;
  top: 0;
  transform: translateX(100%);
  transition: transform ${theme('animations.easeOutQuadTransition')};
  width: 100%;

  ${media.min('ms')`
    width: ${units(74)};
  `}

  ${ifProp('isOpen', css`
    transform: translateX(0);
    transition-delay: 0.2s;
  `)}
`;

/**
 * `SidebarHeader` styled component.
 */

const SidebarHeader = styled.div`
  align-items: center;
  display: grid;
  grid-template-areas: '. title closeButton';
  grid-template-columns: ${units(3.5)} 1fr ${units(3.5)};
  height: ${units(13)};
  justify-items: center;
  padding: 0 49px;

  ${media.min('ms')`
    grid-template-areas: '. title closeButton';
    padding: 0 67px;
  `}
`;

/**
 * `HeaderLabel` styled component.
 */

const HeaderLabel = styled(Type.Paragraph).attrs({ as: 'div' })`
  grid-area: title;
`;

/**
 * `CloseButton` styled component.
 */

const CloseButton = styled.button`
  -webkit-tap-highlight-color: transparent;
  appearance: none;
  background-color: transparent;
  border: none;
  color: ${color('black')};
  cursor: pointer;
  grid-area: closeButton;
  outline: none;
  padding: 0;
  transition: color ${theme('animations.defaultTransition')};

  &:focus,
  &:hover {
    color: ${color('textColor')};
  }
`;

/**
 * `Divider` styled component.
 */

const Divider = styled.div`
  background-color: ${color('grey100')};
  height: 1px;
  margin: 0 ${theme('grid.gutterMobile')}px;

  ${media.min('ms')`
    margin: 0 ${units(6)};;
  `}
`;

/**
 * `SidebarContentWrapper` styled component.
 */

const SidebarContentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
`;

/**
 * `SidebarContent` styled component.
 */

const SidebarContent = styled.div`
  flex: 1;
  padding: 0 ${theme('grid.gutterMobile')}px;

  ${media.min('ms')`
    padding: 0 ${units(6)};
  `}

  div:last-child {
    border-bottom: 0;
  }
`;

/**
 * `SidebarFooter` styled component.
 */

const SidebarFooter = styled.div`
  align-items: center;
  display: grid;
  grid-gap: ${units(1.5)};
  grid-template-columns: repeat(2, max-content);
  height: ${units(13)};
  justify-content: end;
  padding: 0 ${theme('grid.gutterMobile')}px;

  ${media.min('ms')`
    padding: 0 ${units(6)};
  `}
`;

/**
 * `ClearButton` styled component.
 */

const ClearButton = styled.button<{ disabled: boolean }>`
  ${theme('typography.styles.small')}

  -webkit-tap-highlight-color: transparent;
  appearance: none;
  background: none;
  border: 0;
  color: ${color('grey400')};
  cursor: pointer;
  letter-spacing: 1px;
  outline: none;
  padding: ${units(1)};
  text-transform: uppercase;
  transition: color ${theme('animations.defaultTransition')};

  &:focus,
  &:hover {
    color: ${color('grey900')};
  }

  ${ifProp('disabled', css`
    color: ${color('grey600')};
    cursor: default;
    pointer-events: none;
  `)}
`;

/**
 * `FiltersSidebar` component.
 */

const FiltersSidebar = (props: Props): ReactElement => {
  const { children, className, isOpen, isUpdating, onClose, onUpdate } = props;
  const { omitUrl, onSubmit, onSubmitClear, reset } = useFiltersList();
  const [forceUpdate, setForceUpdate] = useState<boolean>();
  const { translate } = useTranslate();
  const router = useRouter();
  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleUpdate = useCallback(filters => {
    if (omitUrl) {
      onClose();
      setForceUpdate(true);

      return;
    }

    const queryString = isEmpty(filters) ? '' : `?${qs.stringify({ filter: filters })}`;

    router.push(
      `${router.pathname}${queryString}`,
      undefined,
      { shallow: true }
    ).then(() => {
      onUpdate();
      onClose();
    });
  }, [omitUrl, onClose, onUpdate, router]);

  const handleClear = useCallback(() => {
    const { submittedFilters } = onSubmitClear();

    handleUpdate(submittedFilters);
  }, [handleUpdate, onSubmitClear]);

  const handleSubmit = useCallback(() => {
    const { submittedFilters } = onSubmit();

    handleUpdate(submittedFilters);
  }, [handleUpdate, onSubmit]);

  useEffect(() => {
    if (omitUrl && forceUpdate) {
      const timeout = setTimeout(() => {
        onUpdate();
        setForceUpdate(false);
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [forceUpdate, omitUrl, onUpdate]);

  return (
    <Wrapper
      className={className}
      isOpen={isOpen}
    >
      <Overlay
        isOpen={isOpen}
        onClick={onClose}
      />

      <Sidebar isOpen={isOpen}>
        <SidebarHeader>
          <HeaderLabel>
            {translate('common:labels.filter')}
          </HeaderLabel>

          <CloseButton onClick={handleClose}>
            <Svg
              icon={closeIcon}
              size={units(3.5)}
            />
          </CloseButton>
        </SidebarHeader>

        <Divider />

        <SidebarContentWrapper>
          <SidebarContent>
            {children}
          </SidebarContent>
        </SidebarContentWrapper>

        <Divider />

        <SidebarFooter>
          <ClearButton
            disabled={isUpdating}
            onClick={handleClear}
          >
            {translate('common:actions.clear')}
          </ClearButton>

          <Button
            disabled={isUpdating}
            isLoading={isUpdating}
            onClick={handleSubmit}
          >
            {translate('common:actions.show')}
          </Button>
        </SidebarFooter>
      </Sidebar>
    </Wrapper>
  );
};

/**
 * Export `FiltersSidebar` component.
 */

export default FiltersSidebar;
